import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Sparkles, Award, RefreshCw, Brain } from 'lucide-react';
import { CoverImage } from '@/components/CoverImage';
import { SaveToTBRButton } from '@/components/SaveToTBRButton';
import { useSaveToTBR } from '@/hooks/useSaveToTBR';
import { DinoAvatar } from '@/components/DinoAvatar';
import { useAwardBookRecommendations } from '@/hooks/useAwardBookRecommendations';
import { useReadingProfile } from '@/hooks/useReadingProfile';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

export default function AwardMatches() {
  const { data: recommendations = [], isLoading, refetch, isRefetching } = useAwardBookRecommendations();
  const { profile, generateProfile, isGenerating } = useReadingProfile();
  const queryClient = useQueryClient();

  // Listen for profile updates and refresh recommendations automatically
  useEffect(() => {
    if (!profile?.user_id) return;

    const channel = supabase
      .channel('profile-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_reading_profiles',
          filter: `user_id=eq.${profile.user_id}`,
        },
        (payload) => {
          console.log('Profile auto-updated:', payload);
          toast.success('Your reading profile was automatically updated from new ratings!');
          queryClient.invalidateQueries({ queryKey: ['readingProfile'] });
          queryClient.invalidateQueries({ queryKey: ['awardBookRecommendations'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.user_id, queryClient]);

  const handleRefresh = async () => {
    toast.promise(
      refetch(),
      {
        loading: 'Getting fresh recommendations from AI...',
        success: (result) => {
          if (result.data?.length === 0) {
            return 'No new matches found. Try generating your profile first!';
          }
          return `Found ${result.data?.length || 0} new matches!`;
        },
        error: 'Failed to get new recommendations'
      }
    );
  };

  const handleGenerateProfile = () => {
    toast.promise(
      new Promise((resolve, reject) => {
        generateProfile(undefined, {
          onSuccess: resolve,
          onError: reject
        });
      }),
      {
        loading: 'Analyzing your reading taste...',
        success: 'Reading profile generated! Refresh to see updated matches.',
        error: (err) => err?.message || 'Failed to generate profile'
      }
    );
  };

  return (
    <main className="container mx-auto px-4 py-8 max-w-6xl">
        <Link to="/awards">
          <Button variant="ghost" className="mb-6 gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Awards
          </Button>
        </Link>

        {/* Hero Section */}
        <div className="mb-8 flex items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-8 h-8 text-primary" />
              <h1 className="text-4xl font-bold">Your Shortlist Matches</h1>
            </div>
            <p className="text-lg text-muted-foreground mb-4">
              AI-powered recommendations from award shortlists, personalized to your reading taste
            </p>
            {recommendations.length > 0 && (
              <p className="text-xs text-muted-foreground mb-4">
                AI analyzed your reading taste to find award-winning matches
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {profile && (
                <Badge variant="secondary" className="text-xs">
                  <Brain className="w-3 h-3 mr-1" />
                  Profile: {profile.total_books_analyzed} books
                </Badge>
              )}
              <Button 
                onClick={handleGenerateProfile}
                variant="outline" 
                size="sm"
                disabled={isGenerating}
                className="gap-2"
              >
                <Brain className={`w-4 h-4 ${isGenerating ? 'animate-pulse' : ''}`} />
                {profile ? 'Update' : 'Generate'} Profile
              </Button>
              <Button 
                onClick={handleRefresh} 
                variant="outline" 
                size="sm"
                disabled={isRefetching}
                className="gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          <DinoAvatar size="xl" className="flex-shrink-0 hidden md:block" />
        </div>

        {/* Matches Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {[...Array(6)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="flex gap-4 mb-4">
                    <Skeleton className="w-32 h-48 rounded" />
                    <div className="flex-1 space-y-3">
                      <Skeleton className="h-6 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-6 w-24" />
                      <Skeleton className="h-2 w-full" />
                      <Skeleton className="h-16 w-full" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : recommendations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {recommendations.map((book, index) => (
              <MatchCard key={`${book.bookId}-${index}`} book={book} />
            ))}
          </div>
        ) : (
          <Card className="mb-8">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground mb-4">
                No award matches found yet. Rate more books to improve your recommendations!
              </p>
              <div className="flex gap-3 justify-center">
                <Link to="/discover">
                  <Button>Discover Books</Button>
                </Link>
                <Link to="/awards">
                  <Button variant="outline">Browse Awards</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="bg-primary/5 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              How AI matches you with award books
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Our AI creates a personalized reading profile from your ratings and reviews, then uses it to find 
              award-shortlisted books that match your unique taste. Generate your profile to get better, faster recommendations.
            </p>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <Award className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                <span><strong>Reading Profile:</strong> AI summarizes your taste from all your rated books</span>
              </div>
              <div className="flex items-start gap-2">
                <Award className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                <span><strong>Concrete Examples:</strong> Uses your highest and lowest rated books for context</span>
              </div>
              <div className="flex items-start gap-2">
                <Award className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                <span><strong>Award Focus:</strong> Only recommends from prestigious award shortlists</span>
              </div>
              <div className="flex items-start gap-2">
                <Award className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                <span><strong>Personalized Explanations:</strong> Each match explains why it fits your reading preferences</span>
              </div>
              <div className="flex items-start gap-2">
                <Award className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary" />
                <span><strong>Match Score:</strong> AI confidence (60-100%) based on how well the book fits your unique reading profile</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
  );
}

function MatchCard({ book }: { book: any }) {
  const { isSaved, toggleSave, loading } = useSaveToTBR(
    book.bookId, 
    undefined, 
    { 
      title: book.title, 
      author: book.author, 
      isbn13: book.isbn13, 
      isbn10: book.isbn10,
      open_library_id: book.open_library_id 
    }
  );

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex gap-4 mb-4">
          <Link to={`/book/${book.bookId}`} className="flex-shrink-0">
            <CoverImage
              bookId={book.bookId}
              title={book.title}
              author={book.author}
              coverUrl={book.coverUrl}
              size="lg"
              className="w-32 h-48 rounded shadow-sm"
            />
          </Link>
          <div className="flex-1 min-w-0">
            <Link to={`/book/${book.bookId}`}>
              <h3 className="font-semibold text-lg mb-1 hover:text-primary line-clamp-2">
                {book.title}
              </h3>
            </Link>
            <p className="text-sm text-muted-foreground mb-3 line-clamp-1">
              {book.author}
            </p>
            {book.awardName && (
              <Badge variant="outline" className="mb-3">
                {book.awardName} {book.awardYear}
              </Badge>
            )}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-secondary rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${book.match_score}%` }}
                  />
                </div>
                <span className="text-sm font-medium text-primary whitespace-nowrap" title="AI confidence score based on your reading profile and preferences">
                  {book.match_score}% match
                </span>
              </div>
              {book.reason && (
                <p className="text-xs text-muted-foreground">
                  <strong>Why:</strong> {book.reason}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <SaveToTBRButton
            isSaved={isSaved}
            onToggle={toggleSave}
            loading={loading}
            size="default"
            variant="outline"
          />
          <Link to={`/book/${book.bookId}`} className="flex-1">
            <Button variant="default" className="w-full">
              View Details
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
